import React, { useState, useEffect } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
    faCalendar,
    faPlus,
    faTrash,
    faSearch,
    faVideo,
    faSpinner,
    faMapMarkerAlt,
    faClock,
    faUsers,
} from '@fortawesome/free-solid-svg-icons';
import { useAuth0 } from '@auth0/auth0-react';
import { toast } from 'react-toastify';
import { eventsService } from '../../services/eventsService';
import EventForm from './EventForm';
import './EventsManagement.css';

function EventsManagement({ currentUser }) {
    const { user } = useAuth0();
    
    // State management
    const [events, setEvents] = useState([]);
    const [filteredEvents, setFilteredEvents] = useState([]);
    const [isLoading, setIsLoading] = useState(false);
    const [showEventForm, setShowEventForm] = useState(false);
    const [selectedEvent, setSelectedEvent] = useState(null);
    const [searchTerm, setSearchTerm] = useState('');
    const [statusFilter, setStatusFilter] = useState('all');
    const [categoryFilter, setCategoryFilter] = useState('all');
    const [timeFilter, setTimeFilter] = useState('all');
    const [currentPage, setCurrentPage] = useState(1);
    const [eventsPerPage] = useState(10);
    const [sortField, setSortField] = useState('EventDate');
    const [sortDirection, setSortDirection] = useState('desc');

    // Filter options
    const [categories, setCategories] = useState([]);
    
    // Choice fields from SharePoint (like Media Centre)
    const [choiceFields, setChoiceFields] = useState({
        categories: [],
        eventTypes: [],
        locations: []
    });
    const [isLoadingChoiceFields, setIsLoadingChoiceFields] = useState(false);

    useEffect(() => {
        fetchEvents();
        fetchChoiceFields(); // Load choice fields on mount
    }, []);

    useEffect(() => {
        applyFilters();
    }, [events, searchTerm, statusFilter, categoryFilter, timeFilter, sortField, sortDirection]);

    const fetchChoiceFields = async () => {
        setIsLoadingChoiceFields(true);
        try {
            const fields = await eventsService.getChoiceFields();
            setChoiceFields(fields);
        } catch (error) {
            console.error('Failed to load events choice fields:', error);
            // Silent fail - form will use fallback options
        } finally {
            setIsLoadingChoiceFields(false);
        }
    };

    const fetchEvents = async () => {
        setIsLoading(true);
        try {
            const eventsData = await eventsService.getAllEvents();
            
            // Process events data
            const processedEvents = eventsData.map(event => ({
                ...event,
                // Ensure we have the fields we need
                ID: event.ID || event.id,
                Title: event.Title || event.title,
                EventDate: event.EventDate ? new Date(event.EventDate) : null,
                CreatedDate: event.Created ? new Date(event.Created) : new Date(),
                ModifiedDate: event.Modified ? new Date(event.Modified) : new Date(),
                // Status based on date and publish flag
                Status: getEventStatus(event),
                // Parse topics if string
                Topics: typeof event.Topics === 'string' ? 
                    event.Topics.split(';').map(t => t.trim()).filter(t => t) : 
                    (event.Topics || []),
                // Map SharePoint field names to form field names
                Sponsors: event.EventSponsors || event.Sponsors || ''
            }));

            setEvents(processedEvents);
            
            // Extract unique categories and types for filters from both events and choice fields
            const eventCategories = [...new Set(processedEvents
                .map(e => e.EventCategory)
                .filter(Boolean))];
            const choiceCategories = choiceFields.categories || [];
            const uniqueCategories = [...new Set([...eventCategories, ...choiceCategories])];
                
            setCategories(uniqueCategories);
            
        } catch (error) {
            console.error('Error fetching events:', error);
            toast.error('Failed to load events');
        } finally {
            setIsLoading(false);
        }
    };

    const getEventStatus = (event) => {
        const now = new Date();
        const eventDate = event.EventDate ? new Date(event.EventDate) : null;
        
        if (!eventDate) return 'Draft';
        if (eventDate > now) return 'Upcoming';
        if (eventDate < now) return 'Past';
        return 'Live';
    };

    const applyFilters = () => {
        let filtered = [...events];

        // Search filter
        if (searchTerm.trim()) {
            const query = searchTerm.toLowerCase();
            filtered = filtered.filter(event =>
                event.Title?.toLowerCase().includes(query) ||
                event.EventDescription_x002f_Narrative?.toLowerCase().includes(query) ||
                event.EventLocation?.toLowerCase().includes(query) ||
                event.Speakers?.toLowerCase().includes(query) ||
                (event.Topics && event.Topics.some(topic => 
                    topic.toLowerCase().includes(query)))
            );
        }

        // Status filter
        if (statusFilter !== 'all') {
            filtered = filtered.filter(event => event.Status === statusFilter);
        }

        // Category filter
        if (categoryFilter !== 'all') {
            filtered = filtered.filter(event => event.EventCategory === categoryFilter);
        }

        // Time filter
        if (timeFilter !== 'all') {
            const now = new Date();
            const oneWeek = 7 * 24 * 60 * 60 * 1000;
            const oneMonth = 30 * 24 * 60 * 60 * 1000;

            filtered = filtered.filter(event => {
                if (!event.EventDate) return false;
                const eventDate = new Date(event.EventDate);
                const timeDiff = eventDate.getTime() - now.getTime();

                switch (timeFilter) {
                    case 'this-week':
                        return timeDiff >= 0 && timeDiff <= oneWeek;
                    case 'this-month':
                        return timeDiff >= 0 && timeDiff <= oneMonth;
                    case 'past-week':
                        return timeDiff < 0 && timeDiff >= -oneWeek;
                    case 'past-month':
                        return timeDiff < 0 && timeDiff >= -oneMonth;
                    default:
                        return true;
                }
            });
        }

        // Apply sorting
        filtered.sort((a, b) => {
            let aValue, bValue;

            switch (sortField) {
                case 'Title':
                    aValue = a.Title || '';
                    bValue = b.Title || '';
                    break;
                case 'EventDate':
                    aValue = a.EventDate ? new Date(a.EventDate) : new Date(0);
                    bValue = b.EventDate ? new Date(b.EventDate) : new Date(0);
                    break;
                case 'EventLocation':
                    aValue = a.EventLocation || '';
                    bValue = b.EventLocation || '';
                    break;
                case 'EventCategory':
                    aValue = a.EventCategory || '';
                    bValue = b.EventCategory || '';
                    break;
                case 'Status':
                    aValue = a.Status || '';
                    bValue = b.Status || '';
                    break;
                default:
                    aValue = a.EventDate ? new Date(a.EventDate) : new Date(0);
                    bValue = b.EventDate ? new Date(b.EventDate) : new Date(0);
            }

            if (aValue < bValue) {
                return sortDirection === 'asc' ? -1 : 1;
            }
            if (aValue > bValue) {
                return sortDirection === 'asc' ? 1 : -1;
            }
            return 0;
        });

        setFilteredEvents(filtered);
        setCurrentPage(1);
    };

    const handleAddEvent = () => {
        setSelectedEvent(null);
        setShowEventForm(true);
    };

    const handleEditEvent = (event) => {
        setSelectedEvent(event);
        setShowEventForm(true);
    };

    const handleDeleteEvent = async (eventId) => {
        if (!window.confirm('Are you sure you want to delete this event?')) {
            return;
        }

        try {
            await eventsService.deleteEvent(eventId);
            toast.success('Event deleted successfully');
            await fetchEvents(); // Refresh list
        } catch (error) {
            console.error('Error deleting event:', error);
            toast.error('Failed to delete event');
        }
    };

    const handleEventSave = async (eventData) => {
        try {
            if (eventData.ID) {
                // Update existing event
                await eventsService.updateEvent(eventData.ID, eventData, user?.name);
                toast.success('Event updated successfully');
            } else {
                // Create new event
                await eventsService.createEvent(eventData, user?.name);
                toast.success('Event created successfully');
            }
            
            setShowEventForm(false);
            setSelectedEvent(null);
            await fetchEvents(); // Refresh list
            
        } catch (error) {
            console.error('Error saving event:', error);
            toast.error('Failed to save event');
            throw error;
        }
    };

    const formatDate = (date) => {
        if (!date) return 'TBC';
        return new Date(date).toLocaleDateString('en-IE', {
            day: '2-digit',
            month: '2-digit',
            year: 'numeric'
        });
    };

    const formatTime = (startTime, endTime) => {
        if (!startTime && !endTime) return 'TBC';
        if (startTime === 'TBC' && endTime === 'TBC') return 'TBC';
        
        const formatTimeString = (timeStr) => {
            if (!timeStr || timeStr === 'TBC') return 'TBC';
            const [h, m] = timeStr.split(':');
            const hour = parseInt(h) % 12 || 12;
            const ampm = parseInt(h) >= 12 ? 'pm' : 'am';
            return `${hour}:${m} ${ampm}`;
        };

        const start = formatTimeString(startTime);
        const end = formatTimeString(endTime);
        
        if (start === 'TBC' && end === 'TBC') return 'TBC';
        return `${start} - ${end}`;
    };

    const getStatusBadgeClass = (status) => {
        switch (status) {
            case 'Upcoming': return 'status-upcoming';
            case 'Past': return 'status-past';
            case 'Live': return 'status-live';
            case 'Draft': return 'status-draft';
            default: return 'status-draft';
        }
    };

    const hasCollateral = (event) => {
        return event.VideoCollateral || event.AudioCollateral || event.FileCollateral;
    };

    // Pagination
    const indexOfLastEvent = currentPage * eventsPerPage;
    const indexOfFirstEvent = indexOfLastEvent - eventsPerPage;
    const currentEvents = filteredEvents.slice(indexOfFirstEvent, indexOfLastEvent);
    const totalPages = Math.ceil(filteredEvents.length / eventsPerPage);

    const paginate = (pageNumber) => setCurrentPage(pageNumber);

    const handleSort = (field) => {
        if (sortField === field) {
            // Toggle direction if same field
            setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
        } else {
            // New field, default to ascending
            setSortField(field);
            setSortDirection('asc');
        }
    };

    const getSortIcon = (field) => {
        if (sortField !== field) {
            return null; // No icon if not the current sort field
        }
        return sortDirection === 'asc' ? '↑' : '↓';
    };

    if (isLoading) {
        return (
            <div className="events-loading">
                <FontAwesomeIcon icon={faSpinner} spin />
                <span>Loading events...</span>
            </div>
        );
    }

    return (
        <div className="events-management">
            <div className="events-management-header">
           
                <button className="btn-add-event" onClick={handleAddEvent}>
                    <FontAwesomeIcon icon={faPlus} /> Add Event
                </button>
            </div>

            {/* Statistics */}
            <div className="events-stats">
                <div className="stat-card">
                    <div className="stat-icon">
                        <FontAwesomeIcon icon={faCalendar} />
                    </div>
                    <div className="stat-details">
                        <h4>Total Events</h4>
                        <p className="stat-number">{events.length}</p>
                    </div>
                </div>
                <div className="stat-card">
                    <div className="stat-icon">
                        <FontAwesomeIcon icon={faClock} />
                    </div>
                    <div className="stat-details">
                        <h4>Upcoming</h4>
                        <p className="stat-number">
                            {events.filter(e => e.Status === 'Upcoming').length}
                        </p>
                    </div>
                </div>
                <div className="stat-card">
                    <div className="stat-icon">
                        <FontAwesomeIcon icon={faVideo} />
                    </div>
                    <div className="stat-details">
                        <h4>With Media</h4>
                        <p className="stat-number">
                            {events.filter(hasCollateral).length}
                        </p>
                    </div>
                </div>
            </div>

            {/* Filters and Search */}
            <div className="events-toolbar">
                <div className="search-box">
                    <FontAwesomeIcon icon={faSearch} />
                    <input
                        type="text"
                        placeholder="Search events..."
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                    />
                </div>

                <div className="filter-group">
                    <select
                        value={statusFilter}
                        onChange={(e) => setStatusFilter(e.target.value)}
                        className="filter-select"
                    >
                        <option value="all">All Status</option>
                        <option value="Upcoming">Upcoming</option>
                        <option value="Past">Past</option>
                        <option value="Live">Live</option>
                        <option value="Draft">Draft</option>
                    </select>

                    <select
                        value={categoryFilter}
                        onChange={(e) => setCategoryFilter(e.target.value)}
                        className="filter-select"
                    >
                        <option value="all">All Categories</option>
                        {categories.map(category => (
                            <option key={category} value={category}>{category}</option>
                        ))}
                    </select>

                    <select
                        value={timeFilter}
                        onChange={(e) => setTimeFilter(e.target.value)}
                        className="filter-select"
                    >
                        <option value="all">All Time</option>
                        <option value="this-week">This Week</option>
                        <option value="this-month">This Month</option>
                        <option value="past-week">Past Week</option>
                        <option value="past-month">Past Month</option>
                    </select>
                </div>
            </div>

            {/* Events Table */}
            <div className="events-table-container">
                {currentEvents.length === 0 ? (
                    <div className="no-events">
                        <FontAwesomeIcon icon={faCalendar} size="3x" />
                        <h4>No Events Found</h4>
                        <p>No events match your current filters.</p>
                        <button className="btn-add-event" onClick={handleAddEvent}>
                            <FontAwesomeIcon icon={faPlus} /> Add Event
                        </button>
                    </div>
                ) : (
                    <>
                        <table className="events-table">
                            <thead>
                                <tr>
                                    <th 
                                        onClick={() => handleSort('Title')}
                                        className={`sortable ${sortField === 'Title' ? 'active' : ''}`}
                                    >
                                        Event {getSortIcon('Title')}
                                    </th>
                                    <th 
                                        onClick={() => handleSort('EventDate')}
                                        className={`sortable ${sortField === 'EventDate' ? 'active' : ''}`}
                                    >
                                        Date & Time {getSortIcon('EventDate')}
                                    </th>
                                    <th 
                                        onClick={() => handleSort('EventLocation')}
                                        className={`sortable ${sortField === 'EventLocation' ? 'active' : ''}`}
                                    >
                                        Location {getSortIcon('EventLocation')}
                                    </th>
                                    <th 
                                        onClick={() => handleSort('EventCategory')}
                                        className={`sortable ${sortField === 'EventCategory' ? 'active' : ''}`}
                                    >
                                        Category {getSortIcon('EventCategory')}
                                    </th>
                                    <th 
                                        onClick={() => handleSort('Status')}
                                        className={`sortable ${sortField === 'Status' ? 'active' : ''}`}
                                    >
                                        Status {getSortIcon('Status')}
                                    </th>
                                    <th>Actions</th>
                                </tr>
                            </thead>
                            <tbody>
                                {currentEvents.map(event => (
                                    <tr key={event.ID} onClick={() => handleEditEvent(event)} style={{ cursor: 'pointer' }}>
                                        <td>
                                            <div className="event-title-cell">
                                                <h4>{event.Title}</h4>
                                                {event.EventDescription_x002f_Narrative && (
                                                    <p className="event-description">
                                                        {event.EventDescription_x002f_Narrative.substring(0, 100)}...
                                                    </p>
                                                )}
                                                {event.Topics && event.Topics.length > 0 && (
                                                    <div className="event-topics">
                                                        {event.Topics.slice(0, 2).map((topic, idx) => (
                                                            <span key={idx} className="topic-tag">{topic}</span>
                                                        ))}
                                                        {event.Topics.length > 2 && (
                                                            <span className="topic-more">+{event.Topics.length - 2}</span>
                                                        )}
                                                    </div>
                                                )}
                                            </div>
                                        </td>
                                        <td>
                                            <div className="event-datetime">
                                                <div className="event-date">
                                                    <FontAwesomeIcon icon={faCalendar} />
                                                    {formatDate(event.EventDate)}
                                                </div>
                                                <div className="event-time">
                                                    <FontAwesomeIcon icon={faClock} />
                                                    {formatTime(event.StartTime, event.EndTime)}
                                                </div>
                                            </div>
                                        </td>
                                        <td>
                                            <div className="event-location">
                                                <FontAwesomeIcon icon={faMapMarkerAlt} />
                                                {event.EventLocation || 'TBC'}
                                            </div>
                                            {event.InPerson_x002f_Virtual && (
                                                <div className="event-type">
                                                    <FontAwesomeIcon icon={faUsers} />
                                                    {event.InPerson_x002f_Virtual}
                                                </div>
                                            )}
                                        </td>
                                        <td>
                                            {event.EventCategory && (
                                                <span className="category-badge">
                                                    {event.EventCategory}
                                                </span>
                                            )}
                                        </td>
                                        <td>
                                            <span className={`status-badge ${getStatusBadgeClass(event.Status)}`}>
                                                {event.Status}
                                            </span>
                                        </td>
                                        <td>
                                            <div className="table-actions">
                                                <button
                                                    className="btn-icon btn-danger"
                                                    onClick={(e) => {
                                                        e.stopPropagation();
                                                        handleDeleteEvent(event.ID);
                                                    }}
                                                    title="Delete Event"
                                                >
                                                    <FontAwesomeIcon icon={faTrash} />
                                                </button>
                                            </div>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>

                        {/* Pagination */}
                        {totalPages > 1 && (
                            <div className="pagination">
                                <button
                                    onClick={() => paginate(currentPage - 1)}
                                    disabled={currentPage === 1}
                                    className="pagination-btn"
                                >
                                    Previous
                                </button>
                                
                                {[...Array(totalPages)].map((_, index) => (
                                    <button
                                        key={index + 1}
                                        onClick={() => paginate(index + 1)}
                                        className={`pagination-btn ${currentPage === index + 1 ? 'active' : ''}`}
                                    >
                                        {index + 1}
                                    </button>
                                ))}
                                
                                <button
                                    onClick={() => paginate(currentPage + 1)}
                                    disabled={currentPage === totalPages}
                                    className="pagination-btn"
                                >
                                    Next
                                </button>
                            </div>
                        )}
                    </>
                )}
            </div>

            {/* Event Form Modal */}
            {showEventForm && (
                <EventForm
                    event={selectedEvent}
                    onSave={handleEventSave}
                    onClose={() => {
                        setShowEventForm(false);
                        setSelectedEvent(null);
                    }}
                    currentUser={currentUser}
                    choiceFields={choiceFields}
                    onChoiceFieldsRefresh={fetchChoiceFields}
                />
            )}
        </div>
    );
}

export default EventsManagement; 